/* eslint-disable max-len */
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
// eslint-disable-next-line import/no-unresolved
import config from 'app-config';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { platformMessages } from 'ml-components-platform-react';
import { MLVehicleInfoLookupService } from 'ml-services-common-react';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';
import { MLAlertGroupComponent } from 'ml-components-common-react';

import messages from './MLPUPEditVehicleComponent.messages';
import ampStyles from './MLPUPEditVehicleComponent.module.scss';
import metadata from './MLPUPEditVehicleComponent.metadata.json5';
import agentStyles from './MLAgentPUPEditVehicleComponent.module.scss';
import agentMetadata from './MLAgentPUPEditVehicleComponent.metadata.json5';
import SelectVehiclePopupComponent from '../../../ml-capability-quoteandbind-common-react/popups/PUPAddVehiclePopupComponent/PUPSelectVehiclePopupComponent';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';


const alertNames = {
    endorsementInvalidVIN: 'endorsementInvalidVIN',
    nonOwnerPolicyError: 'nonOwnerPolicyError'
};

function MLPUPEditVehicleComponent(props) {
    const modalApi = useModal();
    const {
        viewModelService,
        vehicleVM,
        quoteID,
        endorsementState,
        getUnderlyingPolicies,
        getStatesList,
        checkForDuplicateVin,
        checkforNonOwnedPolicy,
        onAddPolicy,
        isGPA,
        isUMB_NP,
        showValidationErrors,
        showProgressDialog,
        hideProgressDialog
    } = props;

    const styles = isGPA ? agentStyles : ampStyles;

    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    const translator = useTranslator();

    const { onValidate } = useValidation('MLPUPEditVehicleComponent');
    const bodyTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('BodyType');
    const jurisdictionTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('Jurisdiction');
    const isOfVehicleType = (category) => {
        return _.get(vehicleVM, "type.value.code", "") === category;
    };
    const isPrivatePassenger = isOfVehicleType('private_passenger_autos_ml');
    const isMotorcycle = isOfVehicleType('Motorcycle');
    const isMoped = isOfVehicleType('Moped');
    const isMotorHome = isOfVehicleType('MotorHome');
    const isOther = isOfVehicleType('Other');
    const isLowSpeedVehicle = isOfVehicleType('LowSpeed_ML');
    const isGolfCart = isOfVehicleType('GolfCart_ML');
    const isATVSnowmobile = isOfVehicleType('atv_snowmobile_ml');
    const isCollectible = isOfVehicleType('collectible_ml');
    const isLightTruck = isOfVehicleType('pickups_ml');
    const isVanSuv = isOfVehicleType('vans_suvs_ml');
    const isPanelTruck = isOfVehicleType('panel_trucks_ml');
    const isFlatbedTruck = isOfVehicleType('flat_bed_box_truck_ml');
    const isOtherPersonallyRegistered = isOfVehicleType('Other_Personally_Registered');

    const [searchVehicleWithVin, setSearchVehicleWithVin] = useState(false);
    const [makes, updatePossibleMakes] = useState([]);
    const [models, updatePossibleModels] = useState([]);
    const [trims, updatetrims] = useState([]);
    const [validVIN, setValidVIN] = useState(true);
    const [vinSuccess, setVinSuccess] = useState(false);
    const [isUnlistedModel, setIsUnlistedModel] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [trimNotFound, setTrimNotFound] = useState(false);
    const [validBodyTypes, setValidBodyTypes] = useState([]);
    const [types, setTypes] = useState([]);
    const [verifyVinClick, setVerifyVinClick] = useState(false);
    const [oldSerial, setOldSerial] = useState(null);
    const [vinCleared, setVinCleared] = useState(false);
    const [savedVinPrefixes, setVinPrefixes] = useState([]);
    const [selectVinPrefixClick, setSelectVinPrefixClick] = useState(false);
    const [priorVinInput, setPriorVinInput] = useState(null);
    const [vehicleChanged, setVehicleChanged] = useState(isGPA ? undefined : false);
    const [vinAlerts, setVinAlerts] = useState([]);
    const [ulAlerts, setUlAlerts] = useState([]);

    const {
        mapVinResponseToVehicle,
        hasErrorCodes,
        clearPupVinFields,
        isValidYMMRequest,
        getHighestCompSymbol,
        setVehicleInfoWithVinPrefix,
        getErrorDescription
    } = MLDriverVehicleUtil();

    const {
        clearErrorAlerts
    } = MLNotificationBlockUtil();


    const getTypeKeyDescription = useCallback((typeKeyName) => {
        return translator({
            id: typeKeyName,
            defaultMessage: typeKeyName
        });
    }, [translator]);

    const getGivenListDropdown = useCallback((typelistCodes) => {
        return typelistCodes.map((type) => {
            return {
                code: type.code,
                name: getTypeKeyDescription(type.name)
            };
        })
    }, [getTypeKeyDescription]);

    const writeValue = useCallback((value, path) => {
        _.set(vehicleVM, path, value);
        refreshData();
    }, [refreshData, vehicleVM]);

    const setCarYears = () => {
        const currentYear = new Date().getUTCFullYear();
        const { oldestCarYear } = config.personalAutoConfig;
        // _.range is not inclusive. Need to add -1 to get correct years
        const yearRange = _.range(currentYear + 1, oldestCarYear - 1);
        const years = yearRange.map(_.toString).map((year) => ({
            code: year,
            name: year
        }));
        return years;
    };

    const hasVin = () => !!vehicleVM.vin.value;

    const isVehicleYearPre1981 = useCallback(() => !!(vehicleVM.value.year && vehicleVM.year.aspects.valid && vehicleVM.year.aspects.subtreeValid && vehicleVM.value.year < 1981), [vehicleVM]);

    const showSerial = () => isLowSpeedVehicle || isGolfCart || isOther || isOtherPersonallyRegistered && !_.get(vehicleVM, 'vin.value');

    const isPrivatePassengerVanOrLightTruck = useCallback(() => isPrivatePassenger || isVanSuv || isLightTruck, [isPrivatePassenger, isVanSuv, isLightTruck]);

    const showVerifyVin = useCallback(() => isPrivatePassengerVanOrLightTruck() || isCollectible || isPanelTruck || isFlatbedTruck, [isPrivatePassengerVanOrLightTruck, isCollectible, isFlatbedTruck, isPanelTruck]);

    const isVinValidationRequired = useCallback(() => showVerifyVin() && vehicleVM.year.value && vehicleVM.year.aspects.valid && vehicleVM.year.aspects.subtreeValid && vehicleVM.year.value > 1981, [showVerifyVin, vehicleVM.year.value, vehicleVM.year.aspects.subtreeValid, vehicleVM.year.aspects.valid]);

    const isLowSpeedVehicleGolfCartOrPersonal = () => isLowSpeedVehicle || isGolfCart || isOtherPersonallyRegistered;

    const isLowSpeedOrGolfCart = () => isLowSpeedVehicle || isGolfCart;

    const isLowSpeedVehicleGolfCartMotorHomeOrATVSnowOrPersonal = () => isLowSpeedVehicle || isGolfCart || isATVSnowmobile || isOtherPersonallyRegistered || isMotorHome;

    const isMotorcycleMopedATVOrOther = () => isMotorcycle || isMoped || isATVSnowmobile || isOther;

    const isMotorcycleOrMoped = () => isMotorcycle || isMoped;

    const showDropdownYear = () => !isLowSpeedVehicleGolfCartMotorHomeOrATVSnowOrPersonal() && !readOnly;

    const showDropdownMake = () => !!((!isLowSpeedVehicleGolfCartMotorHomeOrATVSnowOrPersonal() && !isMotorcycleOrMoped()) && !readOnly &&
        ((makes && makes.length > 0) || _.isUndefined(vehicleVM.year.value)) && !isOtherPersonallyRegistered && !isVehicleYearPre1981());

    const showDropdownModel = () => !!((!isLowSpeedVehicleGolfCartMotorHomeOrATVSnowOrPersonal() && !isMotorcycleOrMoped()) &&
        !isUnlistedModel && !readOnly && ((makes && makes.length > 0) || _.isUndefined(vehicleVM.year.value)));

    const showBodyType = () => {
        if (isGPA) {
            return showVerifyVin();
        }
        return showVerifyVin() || !vehicleVM.type.value;
    }

    const showCCS = () => {
        if (isGPA) {
            return isMotorcycle || isMoped || isATVSnowmobile || isOther;
        }
        return isMotorcycleMopedATVOrOther();
    }

    const showVinField = useCallback(() => !!((showVerifyVin() && !isOther &&
        (!vehicleVM.year.value) || (vehicleVM.year.value && makes && makes.length > 0)) && !isOtherPersonallyRegistered), [isOther, isOtherPersonallyRegistered, showVerifyVin, makes, vehicleVM.year.value]);

    const showUnlistedModel = () => {
        return showVerifyVin() && !isVehicleYearPre1981();
    }

    const isFreeTextTrimFieldVisible = () => {
        if (isUnlistedModel || readOnly) {
            return true;
        }

        if (isGPA && (!vehicleVM.year.value || !vehicleVM.model.value && !isVehicleYearPre1981())) {
            return false;
        }

        if (isLowSpeedVehicleGolfCartMotorHomeOrATVSnowOrPersonal() || isMotorcycleOrMoped()) {
            return false;
        }

        if (isVehicleYearPre1981()) {
            return true;
        }

        if (isGPA) {
            return trims.length === 0 || !!vehicleVM.value.isVinValidated;
        }

        return trims.length === 0 || (trims.length > 0 && readOnly) || !vehicleChanged;
    };

    const isTrimOptionsFieldVisible = () => {
        if (isLowSpeedOrGolfCart() || isOtherPersonallyRegistered || isUnlistedModel || readOnly || isVehicleYearPre1981()) {
            return false;
        }

        if (isGPA) {
            return trims.length > 0 && !readOnly && !vehicleVM.value.isVinValidated;
        }

        return trims.length > 0 && !readOnly;
    };

    const toggleVinSuccess = useCallback(() => {
        setVinSuccess(!vinSuccess);
    }, [vinSuccess]);

    const toggleSearchVehicleWithVin = useCallback(() => {
        setSearchVehicleWithVin(!searchVehicleWithVin);
    }, [searchVehicleWithVin]);

    const invalidateCurrentVin = useCallback(() => {
        setValidVIN(null);
        setReadOnly(false);
        setVerifyVinClick(false);
        vehicleVM.value.error.message = undefined;
        setVinAlerts([]);
    }, [vehicleVM.value.error]);

    const clearVehicleFields = useCallback(() => {
        clearPupVinFields(vehicleVM);
        invalidateCurrentVin();
        setSearchVehicleWithVin(false);
    }, [clearPupVinFields, invalidateCurrentVin, vehicleVM])

    const onClickCancelVin = useCallback(() => {
        toggleVinSuccess();
        invalidateCurrentVin();
        toggleSearchVehicleWithVin();
    }, [toggleVinSuccess, invalidateCurrentVin, toggleSearchVehicleWithVin]);

    const setVehicleInfoWithTrim = useCallback((trim) => {
        if (savedVinPrefixes && savedVinPrefixes.length !== 0 && !readOnly) {
            setSelectVinPrefixClick(true);
            const filteredVinPrefixes = savedVinPrefixes.filter((vinPrefix) => vinPrefix.trim === trim);
            if (filteredVinPrefixes.length === 1) {
                setVehicleInfoWithVinPrefix(filteredVinPrefixes[0], vehicleVM.value);
            } else {
                const prefixWithHighestCompSymbol = getHighestCompSymbol(filteredVinPrefixes);
                setVehicleInfoWithVinPrefix(prefixWithHighestCompSymbol, vehicleVM.value);
            }
        }
    }, [getHighestCompSymbol, readOnly, savedVinPrefixes, setVehicleInfoWithVinPrefix, vehicleVM.value]);

    const lookupVIN = useCallback(async (vehicle) => {
        const vehicleYear = vehicle.year.value ? vehicle.year.value.toString() : null;
        const req = {
            year: vehicleYear,
            makeCode: vehicle.makeCode.value,
            modelCode: vehicle.modelCode.value,
            quoteID: quoteID
        };

        if (isValidYMMRequest(req)) {
            showProgressDialog(platformMessages.loadingTrims);
            try {
                const result = await MLVehicleInfoLookupService.lookupVin(req, authHeader);
                hideProgressDialog();
                return result.vinPrefixes;
            } catch (err) {
                hideProgressDialog();
                const errorMessage = translator(messages.vinLookupErrorMessage);
                vehicleVM.value.error.message = errorMessage;
                setVinAlerts([{ type: 'error', message: errorMessage, block: alertNames.endorsementInvalidVIN }]);
                return [];
            }
        }
        return [];
    }, [authHeader, hideProgressDialog, quoteID, isValidYMMRequest, showProgressDialog, translator, vehicleVM.value.error]);

    const getCleanVinPrefixes = (vinPrefixes) => {
        const prefixesWithTrim = vinPrefixes.filter((prefix) => !_.isEmpty(prefix.trim));
        return prefixesWithTrim;
    };

    const getTrimsFromVinPrefixes = useCallback((vinPrefixes) => {
        if (vinPrefixes) {
            const cleanPrefixes = getCleanVinPrefixes(vinPrefixes);
            setVinPrefixes(cleanPrefixes);
            const auxTrims = [];
            const result = [];
            cleanPrefixes.forEach((vinPrefix) => {
                if (auxTrims.indexOf(vinPrefix.trim) === -1 && (vinPrefix.trim && vinPrefix.trim.length > 0)) {
                    if (isGPA || (!isGPA && vehicleVM.bodyType && vehicleVM.bodyType.value && vinPrefix.bodyType === vehicleVM.bodyType.value.code)) {
                        auxTrims.push(vinPrefix.trim);
                        result.push({
                            code: vinPrefix.trim,
                            name: vinPrefix.trim
                        })
                    }
                }
            });
            return result;
        }
    }, [isGPA, vehicleVM.bodyType])

    const fetchMakes = useCallback(async (vehicle) => {
        if (vehicle.year.value) {
            const req = {
                year: vehicle.year.value
            };
            showProgressDialog(platformMessages.loadingMakes);
            const result = await MLVehicleInfoLookupService.lookupMakes(req, authHeader);
            const makeCodes = result.makesCodes.map((make) => ({
                code: make.code,
                name: make.description
            }));
            hideProgressDialog();
            return makeCodes;
        }
        return [];
    }, [authHeader, hideProgressDialog, showProgressDialog]);

    const fetchModels = useCallback(async (vehicle) => {
        if (vehicle.year.value && vehicle.makeCode.value) {
            const req = {
                year: vehicle.year.value,
                makeCode: vehicle.makeCode.value
            };
            showProgressDialog(platformMessages.loadingModels);
            const result = await MLVehicleInfoLookupService.lookupModels(req, authHeader);
            const modelCodes = result.models.map((model) => ({
                code: model.code,
                name: model.description
            }));
            hideProgressDialog();

            return modelCodes;
        }
        return [];
    }, [authHeader, hideProgressDialog, showProgressDialog]);

    const getMakenameByCode = useCallback((makeCode, makeList) => {
        const filtered = makeList.filter((make) => make.code === makeCode);
        if (filtered.length > 0) {
            return filtered[0].name;
        }
        return null;
    }, []);

    const getModelnameByCode = useCallback((modelname, modelList) => {
        const filtered = modelList.filter((make) => make.code === modelname);
        if (filtered.length > 0) {
            return filtered[0].name;
        }
        return null;
    }, []);

    const getCleanVinBodyTypes = (vinPrefixes) => {
        const prefixesWithBodyType = vinPrefixes.filter((prefix) => !_.isEmpty(prefix.bodyType));
        return prefixesWithBodyType;
    };

    const getBodyTypesFromVinPrefixes = useCallback((vinPrefixes) => {
        if (isUnlistedModel) {
            return getGivenListDropdown(bodyTypelist.codes);
        }
        if (vinPrefixes) {
            const cleanPrefixes = getCleanVinBodyTypes(vinPrefixes);
            setVinPrefixes(cleanPrefixes);
            const auxBodyTypes = [];
            cleanPrefixes.forEach((vinPrefix) => {
                if (auxBodyTypes.indexOf(vinPrefix.bodyType) === -1 && (vinPrefix.bodyType && vinPrefix.bodyType.length > 0)) {
                    auxBodyTypes.push(vinPrefix.bodyType);
                }
            });

            if (!isUnlistedModel) {
                const filteredCodes = bodyTypelist.codes.filter((bType) => auxBodyTypes.includes(bType.code));
                return getGivenListDropdown(filteredCodes);
            }
            return getGivenListDropdown(bodyTypelist.codes);

            // MLDriverVehicleUtil.setVinIntegrationErrorCode($scope.yymResponse.error, $scope.vehicle.value);
        }
    }, [getGivenListDropdown, bodyTypelist.codes, isUnlistedModel]);

    const updateModel = useCallback(async () => {
        if (!readOnly) {
            vehicleVM.bodyType.value = undefined;
            vehicleVM.trim_ML.value = undefined;
            if (isVinValidationRequired()) {
                vehicleVM.vin.value = undefined;
            }
            setVerifyVinClick(false);
            if (!isGPA) {
                const vinPrefixes = await lookupVIN(vehicleVM);
                const trimCodes = getTrimsFromVinPrefixes(vinPrefixes);
                updatetrims(trimCodes);
            }
        }
    }, [getTrimsFromVinPrefixes, isGPA, isVinValidationRequired, lookupVIN, readOnly, vehicleVM]);

    const updateMake = useCallback(() => {
        if (!readOnly) {
            vehicleVM.model.value = undefined;
            vehicleVM.modelCode.value = undefined;
            updateModel();
        }
    }, [readOnly, updateModel, vehicleVM.model, vehicleVM.modelCode]);

    const updateYear = useCallback(() => {
        if (!readOnly) {
            if (isVehicleYearPre1981()) {
                setValidBodyTypes(getGivenListDropdown(bodyTypelist.codes));
            }
            vehicleVM.make.value = undefined;
            vehicleVM.makeCode.value = undefined;
            updateMake();
        }
    }, [bodyTypelist.codes, getGivenListDropdown, isVehicleYearPre1981, readOnly, updateMake, vehicleVM.make, vehicleVM.makeCode]);

    const onYearSelect = useCallback(async (value, path) => {
        _.set(vehicleVM, path, value);
        if (!_.isEmpty(value)) {
            const makeCodes = await fetchMakes(vehicleVM);
            updatePossibleMakes(makeCodes);

            if (!validVIN && isVinValidationRequired()) {
                vehicleVM.vin.value = undefined;
                setVinCleared(true);
                if (!isGPA) {
                    setVehicleChanged(true);
                }
            }
        }
        updateYear();
        refreshData();
    }, [vehicleVM, updateYear, refreshData, fetchMakes, validVIN, isVinValidationRequired, isGPA]);

    const onMakeSelect = useCallback(async (value, path) => {
        _.set(vehicleVM, path, value);
        if (!_.isEmpty(value)) {
            if (!validVIN) {
                vehicleVM.model.value = undefined;
                vehicleVM.modelCode.value = undefined;
                if (!isGPA) {
                    setVehicleChanged(true);
                }
                if (isVinValidationRequired()) {
                    vehicleVM.vin.value = undefined;
                    setVinCleared(true);
                }
            }
            const makeName = getMakenameByCode(value, makes);
            _.set(vehicleVM, 'make', makeName);
            const modelCodes = await fetchModels(vehicleVM);
            updatePossibleModels(modelCodes);
        }
        updateMake();
        refreshData();
    }, [fetchModels, getMakenameByCode, isGPA, isVinValidationRequired, makes, refreshData, updateMake, validVIN, vehicleVM]);

    const onModelSelect = useCallback(async (value, path) => {
        _.set(vehicleVM, path, value);
        if (!_.isEmpty(value)) {
            const modelName = getModelnameByCode(value, models);
            _.set(vehicleVM, 'model', modelName);
            const vinPrefixes = await lookupVIN(vehicleVM);
            const trimCodes = getTrimsFromVinPrefixes(vinPrefixes);
            updatetrims(trimCodes);
            if (!isGPA) {
                const bTypes = getBodyTypesFromVinPrefixes(vinPrefixes);
                setValidBodyTypes(bTypes);
            }
            if (!validVIN && isVinValidationRequired()) {
                vehicleVM.vin.value = undefined;
                setVinCleared(true);
                if (!isGPA) {
                    setVehicleChanged(true);
                }
            }
        }
        updateModel();
        refreshData();
    }, [getBodyTypesFromVinPrefixes, getModelnameByCode, getTrimsFromVinPrefixes, isGPA, isVinValidationRequired, lookupVIN, models, refreshData, updateModel, validVIN, vehicleVM]);

    const onModelCheckBoxSelect = useCallback((value) => {
        setIsUnlistedModel(value);
        if (value === true) {
            setReadOnly(false);
        }
    }, []);

    const onTypeSelect = useCallback((value, path) => {
        _.set(vehicleVM, path, value);
        if (!_.isUndefined(value)) {
            setIsUnlistedModel(false);
            clearVehicleFields();
            if (!showVinField() || !showVerifyVin()) {
                vehicleVM.value.isVinValidated = true;
            }
        }
        refreshData();
    }, [refreshData, showVerifyVin, showVinField, clearVehicleFields, vehicleVM]);

    const onBodyTypeSelect = useCallback(async (value, path) => {
        _.set(vehicleVM, path, value);
        vehicleVM.trim_ML.value = undefined;
        if (isVinValidationRequired()) {
            vehicleVM.vin.value = undefined;
        }
        const vinPrefixes = await lookupVIN(vehicleVM);
        const trimCodes = getTrimsFromVinPrefixes(vinPrefixes);
        updatetrims(trimCodes);
        refreshData();
    }, [getTrimsFromVinPrefixes, isVinValidationRequired, lookupVIN, refreshData, vehicleVM]);

    const onTrimSelect = useCallback((value, path) => {
        _.set(vehicleVM, path, value);
        if (!_.isEmpty(value)) {
            setVehicleInfoWithTrim(value);
        }
        refreshData();
    }, [setVehicleInfoWithTrim, refreshData, vehicleVM]);

    const checkUlPolicy = useCallback(() => {
        const ulPolicyFixID = _.get(vehicleVM, 'ulPolicyFixID.value');
        if (ulPolicyFixID && checkforNonOwnedPolicy(ulPolicyFixID)) {
            const errorMessage = translator(messages.nonOwnerPolicyError);
            vehicleVM.value.error.ulMessage = errorMessage;
            setUlAlerts([{ type: 'error', message: errorMessage, block: alertNames.nonOwnerPolicyError }]);
        } else {
            vehicleVM.value.error.ulMessage = undefined;
            setUlAlerts([]);
        }
    }, [checkforNonOwnedPolicy, translator, vehicleVM]);

    const onPolicyInfoChange = useCallback((value, path) => {
        _.set(vehicleVM, path, value);
        refreshData();
        if (value === 'new') {
            onAddPolicy(checkUlPolicy);
        } else {
            checkUlPolicy();
        }

    }, [checkUlPolicy, onAddPolicy, refreshData, vehicleVM]);

    const handleLookupVinError = (vehicleInfo) => {
        clearPupVinFields(vehicleVM);
        const errorMessage = (!_.isEmpty(vehicleInfo.error) && !_.isEmpty(vehicleInfo.error.code) && vehicleInfo.error.code === 'N')
        ? getErrorDescription(vehicleInfo) : `${`VIN  ${vehicleVM.vin.value}`}${translator(messages.vinResponseInValid)}`;
        setVinAlerts([{ type: 'error', message: errorMessage, block: alertNames.endorsementInvalidVIN }]);
    }

    const mapVehicleRelatedTypelists = useCallback(async () => {
        const makeCodes = await fetchMakes(vehicleVM);
        updatePossibleMakes(makeCodes);
        const makeName = getMakenameByCode(vehicleVM.value.makeCode, makeCodes);
        _.set(vehicleVM, 'make', makeName);

        const modelCodes = await fetchModels(vehicleVM);
        updatePossibleModels(modelCodes);
        const modelName = getModelnameByCode(vehicleVM.value.modelCode, modelCodes);
        _.set(vehicleVM, 'model', modelName);

        const vinPrefixes = await lookupVIN(vehicleVM);
        const trimCodes = getTrimsFromVinPrefixes(vinPrefixes);
        updatetrims(trimCodes);

        if (!isGPA) {
            const bTypes = getBodyTypesFromVinPrefixes(vinPrefixes);
            setValidBodyTypes(bTypes);
        }
    }, [fetchMakes, fetchModels, getBodyTypesFromVinPrefixes, getMakenameByCode, getModelnameByCode, getTrimsFromVinPrefixes, isGPA, lookupVIN, vehicleVM]);

    const verifyVIN = useCallback(async () => {
        setVerifyVinClick(true);
        if (vehicleVM.vin.value && vehicleVM.vin.value.length > 0) {
            const req = {
                vin: vehicleVM.vin.value,
                quoteID: quoteID
            };
            const vehicleInfo = await MLVehicleInfoLookupService.verifyVin(req, authHeader);
            if (vehicleInfo && vehicleInfo.make && vehicleInfo.model && vehicleInfo.year && !hasErrorCodes(vehicleInfo)) {
                clearErrorAlerts();
                setValidVIN(true);
                setVinSuccess(true);
                vehicleVM.value.isVinValidated = true;
                mapVinResponseToVehicle(vehicleInfo, vehicleVM.value);
                mapVehicleRelatedTypelists();
                vehicleVM.value.hasDuplicateVins = checkForDuplicateVin(vehicleVM.value.vin);
                vehicleVM.value.error.message = undefined;
                setVinAlerts([]);
                setReadOnly(true);
                if (vehicleInfo.trim) {
                    setTrimNotFound(false);
                } else {
                    setTrimNotFound(true);
                }
            } else {
                setValidVIN(false);
                setReadOnly(false);
                setTrimNotFound(false);
                handleLookupVinError(vehicleInfo);
            }
        } else {
            setReadOnly(false);
            setTrimNotFound(false);
        }
    }, [vehicleVM, quoteID, authHeader, hasErrorCodes, mapVinResponseToVehicle, mapVehicleRelatedTypelists, checkForDuplicateVin, handleLookupVinError]);


    const validateSerialNumberWithVin = useCallback(() => {
        vehicleVM.serialNumber.value = vehicleVM.serialNumber.value ? vehicleVM.serialNumber.value.toUpperCase() : undefined;
        if (vehicleVM.serialNumber.value !== oldSerial && vehicleVM.value.type === 'Other' && vehicleVM.vin.value) {
            vehicleVM.vin.value = undefined;
        }
        refreshData();
    }, [oldSerial, refreshData, vehicleVM.serialNumber, vehicleVM.value.type, vehicleVM.vin]);

    const getVinHelpLabel = () => {
        return (isATVSnowmobile || isMotorcycle || isOther) ? messages.findSerialNumber : messages.findVin;
    }

    const getVinHelpMessage = () => {
        return (isATVSnowmobile || isMotorcycle || isOther) ? messages.whereVinSerialAns : messages.whereVinAns;
    }

    const getVehicleTypeHelpMessage = useCallback(() => {
        let state = '';
        const policyState = vehicleVM.value.policyState;
        if (!_.isUndefined(policyState) && ['NH'].indexOf(jurisdictionTypelist.getCode(policyState).code) > -1) {
            state = ` ${policyState}`;
        }
        return <FormattedMessage
            {...messages.vehicleTypeTooltipContent}
            values={{
                ulSection: (
                    <ul className='styled-ul-list-basic no-inner-margin vertical-spacing-0'>
                        <li>{translator(messages.whatIsPrivatePassengerLightTruck)}</li>
                        <li>{translator(messages.whatIsPickupTruck)}</li>
                        <li>{translator(messages.whatIsVanSUV)}</li>
                        <li>{translator(messages.whatIsPanelTruck)}</li>
                        <li>{translator(messages.whatIsFlatBedOrBoxTruck)}</li>
                        <li>{translator(messages.whatIsCollectible)}</li>
                        <li>{translator(messages.whatIsAntique)}</li>
                        <li>{translator(messages.whatIsClassic)}</li>
                        <li>{translator(messages.whatIsReplicaReproductions)}</li>
                        <li>{translator(messages.whatIsCustom)}</li>
                        <li>{translator(messages.whatIsLowSpeed)}</li>
                        <li>{translator(messages.whatIsGolfCart)}</li>
                        <li>{translator(messages.whatIsATVSnowmobile)}</li>
                        <li>{translator(messages.whatIsTrailers)}</li>
                    </ul>
                ),
                policyState: state
            }}
        />
    }, [jurisdictionTypelist, translator, vehicleVM.value.policyState])

    const getVehicleTypeHelpMessageForAgent = useCallback(() => {
        return <FormattedMessage
            {...messages.vehicleTypeTooltipContentForAgent}
            values={{
                ulSection: (
                    <ul className='styled-ul-list-basic no-inner-margin vertical-spacing-0'>
                        <li>{translator(messages.whatIsPrivatePassengerLightTruck)}</li>
                        <li>{translator(messages.whatIsPickupTruck)}</li>
                        <li>{translator(messages.whatIsVanSUV)}</li>
                        <li>{translator(messages.whatIsPanelTruck)}</li>
                        <li>{translator(messages.whatIsFlatBedOrBoxTruck)}</li>
                        <li>{translator(messages.whatIsCollectible)}</li>
                        <li>{translator(messages.whatIsAntique)}</li>
                        <li>{translator(messages.whatIsClassic)}</li>
                        <li>{translator(messages.whatIsReplicaReproductions)}</li>
                        <li>{translator(messages.whatIsCustom)}</li>
                        <li>{translator(messages.whatIsLowSpeed)}</li>
                        <li>{translator(messages.whatIsGolfCart)}</li>
                        <li>{translator(messages.whatIsATVSnowmobile)}</li>
                        <li>{translator(messages.whatIsTrailers)}</li>
                        {endorsementState === 'NH' && <li>{translator(messages.whatIsOtherVehicles)}</li>}
                    </ul>
                )
            }}
        />
    }, [endorsementState, translator])

    const isPartialVinValid = () => {
        return !!(vehicleVM.year && vehicleVM.value.make && vehicleVM.modelCode && vehicleVM.value.bodyType && vehicleVM.value.trim_ML);
    }

    const vinValid = () => {
        return (!!(validVIN && verifyVinClick));
    }

    const canClearVinFields = (vehicle) => {
        return !selectVinPrefixClick && !vehicle.value.fixedId;
    }

    const validateVinWithSerialNumber = (vehicle) => {
        vehicle.vin.value = vehicle.vin.value ? vehicle.vin.value.toUpperCase() : null;
        setSelectVinPrefixClick(false);
        if (vehicle.vin.value !== priorVinInput && canClearVinFields(vehicle) && !vinCleared && !isVehicleYearPre1981() && !isMotorcycle) {
            clearPupVinFields(vehicle);
            setValidVIN(false);
        }
        if (!!(vehicle.type.value) && vehicle.type.value.code === 'Other' && vehicle.serialNumber && vehicle.vin.value) {
            vehicle.serialNumber.value = null;
        }
        setReadOnly(false);
        setVerifyVinClick(false);
    };

    const onFocusVinNumber = () => {
        setPriorVinInput(vehicleVM.vin.value);
    };

    const onBlurVinNumber = () => {
        const vinNumber = vehicleVM.vin.value;
        if (vinNumber !== priorVinInput) {
            setValidVIN(false);
            setVerifyVinClick(false);
            vehicleVM.value.hasDuplicateVins = false;
            if (vinNumber !== null && !_.isUndefined(vinNumber)) {
                setVinCleared(false);
            }
        }
        validateVinWithSerialNumber(vehicleVM);
    };

    const hasYear = () => {
        return !!vehicleVM.year.value;
    };

    const isPrefillFullVinVerified = () => {
        return !!((vehicleVM.vin.value && vehicleVM.vin.value.length === 17) && vehicleVM.value.collisionSymbol_ML && vehicleVM.value.comprehensiveSymbol_ML);
    };

    const selectVinPrefix = (selectedVin) => {
        setSelectVinPrefixClick(true);
        vehicleVM.bodyType.value = selectedVin.bodyType;
        vehicleVM.trim_ML.value = selectedVin.trim;
        vehicleVM.vin.value = selectedVin.vinPrefix;
        vehicleVM.value.error.message = undefined;
        setVinAlerts([]);
        refreshData();
    }

    const openSelectVehiclePopup = (vins) => {
        modalApi.showModal(
            <SelectVehiclePopupComponent vins={vins} />
        ).then((selectedVin) => {
            selectVinPrefix(selectedVin);
        });
    }

    const lookUpVinButton = async () => {
        const vinPrefixes = await lookupVIN(vehicleVM);

        if (vinPrefixes.length > 0) {
            vinPrefixes.forEach((vinVehicle) => {
                vinVehicle.checked = false;
            });
            const vins = getCleanVinPrefixes(vinPrefixes);
            openSelectVehiclePopup(vins);
        }
    }

    const overrideProps = {
        errorContainer: {
            visible: !_.isEmpty(vinAlerts) || !_.isEmpty(ulAlerts)
        },
        vinErrorContainer: {
            visible: !_.isEmpty(vinAlerts)
        },
        vinAlertComponent: {
            customAlerts: vinAlerts,
            isAmpPopup: true
        },
        ulErrorContainer: {
            visible: !_.isEmpty(ulAlerts)
        },
        ulAlertComponent: {
            customAlerts: ulAlerts,
            isAmpPopup: true
        },
        vehicletype: {
            availableValues: types,
            onValueChange: onTypeSelect
        },
        vehicletypeTooltip: {
            tooltipLabel: translator(messages.vehicleTypeTooltipHeader),
            tooltipContent: getVehicleTypeHelpMessage(),
            tooltipContentClassName: styles.vehicleTypeTooltipContent,
            tooltipHeaderClassName: styles.vehicleTypeTooltipHeader
        },
        describeOtherContainer: {
            visible: isOther
        },
        yearContainer: {
            className: _.get(vehicleVM, 'value.isAgentCreated', false) ? 'col-md-2 vertical-spacing-40' : 'col-md-6 vertical-spacing-40'
        },
        yearDropdown: {
            availableValues: setCarYears(),
            onValueChange: onYearSelect,
            visible: showDropdownYear(),
            disabled: readOnly
        },
        yearText: {
            visible: !showDropdownYear(),
            disabled: readOnly
        },
        makeDropdownContainer: {
            visible: showDropdownMake(),
            className: _.get(vehicleVM, 'value.isAgentCreated', false) ? 'col-md-2 vertical-spacing-40' : 'col-md-6 vertical-spacing-40'
        },
        makeTextContainer: {
            visible: !showDropdownMake()
        },
        makeDropdown: {
            availableValues: makes,
            onValueChange: onMakeSelect,
            disabled: readOnly,
            validationMessages: [...vehicleVM.make.aspects.validationMessages]
        },
        makeText: {
            disabled: readOnly
        },
        vehicleModelTypelistContainer: {
            visible: showDropdownModel(),
            className: showUnlistedModel() ? 'vertical-spacing-10' : 'vertical-spacing-40'
        },
        vehicleModelTypelist: {
            availableValues: models,
            disabled: readOnly,
            onValueChange: onModelSelect,
            validationMessages: [...vehicleVM.model.aspects.validationMessages]
        },
        vehicleModelTextContainer: {
            visible: !showDropdownModel(),
            className: showUnlistedModel() ? 'vertical-spacing-10' : 'vertical-spacing-40'
        },
        vehicleModelText: {
            disabled: readOnly
        },
        bodyTypeContainer: {
            visible: showBodyType()
        },
        bodyTypeDropdown: {
            availableValues: validBodyTypes.length > 0 ? validBodyTypes : getGivenListDropdown(bodyTypelist.codes),
            disabled: readOnly && vehicleVM.trim_ML.value !== undefined,
            onValueChange: onBodyTypeSelect
        },
        unlistedModelContainer: {
            visible: showUnlistedModel()
        },
        myModelListedCheck: {
            onValueChange: onModelCheckBoxSelect,
            value: isUnlistedModel,
        },
        trimDropdownContainer: {
            visible: isTrimOptionsFieldVisible() && vehicleChanged
        },
        trimDropdown: {
            availableValues: trims,
            onValueChange: onTrimSelect,
            disabled: trims.length === 1 && vehicleVM.trim_ML.value !== undefined && !vehicleChanged
        },
        trimTextContainer: {
            visible: isFreeTextTrimFieldVisible()
        },
        trimText: {
            disabled: readOnly && !trimNotFound && !vehicleChanged
        },
        vinNumberTipContainer: {
            visible: !!vehicleVM.type.value && ((!showVinField() && !showSerial()) || isOther) && !vehicleVM.serialNumber.value
        },
        vinNumber: {
            onFocus: onFocusVinNumber,
            onBlur: onBlurVinNumber
        },
        vinNumberTooltip: {
            tooltipLabel: translator(getVinHelpLabel()),
            tooltipContent: translator(getVinHelpMessage())
        },
        serialNumberTipContainer: {
            visible: showSerial()
        },
        serialNumber: {
            onBlur: validateSerialNumberWithVin
        },
        serialNumberTooltip: {
            tooltipLabel: translator(messages.findSerialNumber),
            tooltipContent: translator(messages.whereVinSerialAns)
        },
        licenseStateContentContainer: {
            className: _.get(vehicleVM, 'value.isAgentCreated', false) ? 'col-md-2' : 'col-md-6'
        },
        ccsRowContainer: {
            visible: showCCS()
        },
        horsepowerRowContainer: {
            visible: isOther
        },
        vinContainer: {
            visible: showVinField()
        },
        findVinHelpText: {
            visible: searchVehicleWithVin
        },
        addVinContainer: {
            visible: !searchVehicleWithVin
        },
        addMyVin: {
            onClick: toggleSearchVehicleWithVin
        },
        vinrow: {
            visible: searchVehicleWithVin
        },
        invalidVinErrorText: {
            visible: !validVIN && verifyVinClick
        },
        dupVinErrorText: {
            visible: vehicleVM.value.hasDuplicateVins === true
        },
        lookUpVinButton: {
            visible: !vinSuccess || !validVIN,
            onClick: verifyVIN
        },
        verifyVinGreenCheck: {
            visible: !!validVIN && vinSuccess
        },
        cancelVINButton: {
            onClick: onClickCancelVin
        },
        policyInfo: {
            availableValues: getUnderlyingPolicies(),
            onValueChange: onPolicyInfoChange
        }
    };

    const agentOverrideProps = {
        vinErrorContainer: {
            visible: !_.isEmpty(vinAlerts)
        },
        vinAlertComponent: {
            customAlerts: vinAlerts,
            isAmpPopup: true
        },
        ulErrorContainer: {
            visible: !_.isEmpty(ulAlerts)
        },
        ulAlertComponent: {
            customAlerts: ulAlerts
        },
        verifyVinGreenCheck: {
            visible: vinValid()
        },
        invalidVinError: {
            visible: !validVIN && verifyVinClick
        },
        duplicatedVinError: {
            visible: vehicleVM.value.hasDuplicateVins === true
        },
        vinRowContainer: {
            visible: !!vehicleVM.type && !isLowSpeedVehicleGolfCartOrPersonal()
        },
        vinNumberInput: {
            onFocus: onFocusVinNumber,
            onBlur: onBlurVinNumber
        },
        verifyVinButton: {
            visible: showVerifyVin(),
            onClick: verifyVIN
        },
        serialNumberRowContainer: {
            visible: isLowSpeedVehicleGolfCartOrPersonal() || isOther
        },
        serialNumberInput: {
            onBlur: validateSerialNumberWithVin
        },
        vehicletype: {
            availableValues: types,
            onValueChange: onTypeSelect
        },
        vehcileTypeTooltipComponent: {
            tooltipHeader: translator(messages.vehicleTypeTooltipHeader),
            tooltipContent: getVehicleTypeHelpMessageForAgent(),
            tooltipContentClassName: styles.vehicleTypeTooltipContent,
            tooltipHeaderClassName: styles.vehicleTypeTooltipHeader,
            noWrapper: true
        },
        describeOtherContainer: {
            visible: isOther
        },
        yearDropdownContainer: {
            visible: showDropdownYear(),
            className: _.get(vehicleVM, 'value.isAgentCreated', false) ? 'col-md-2 vertical-spacing-40' : 'col-md-3 vertical-spacing-40'
        },
        yearDropdown: {
            availableValues: setCarYears(),
            onValueChange: onYearSelect,
            disabled: readOnly
        },
        yearTextContainer: {
            visible: !showDropdownYear(),
            className: _.get(vehicleVM, 'value.isAgentCreated', false) ? 'col-md-2 vertical-spacing-40' : 'col-md-3 vertical-spacing-40'
        },
        yearText: {
            disabled: readOnly
        },
        makeDropdownContainer: {
            visible: showDropdownMake()
        },
        makeDropdown: {
            availableValues: makes,
            onValueChange: onMakeSelect,
            disabled: readOnly
        },
        makeTextContainer: {
            visible: !showDropdownMake()
        },
        makeText: {
            disabled: readOnly
        },
        modelCheckboxContainer: {
            className: _.get(vehicleVM, 'value.isAgentCreated', false) ? 'col-md-2' : 'col-md-3'
        },
        modelDropdownContainer: {
            visible: showDropdownModel()
        },
        modelDropdown: {
            availableValues: models,
            onValueChange: onModelSelect,
            disabled: readOnly
        },
        modelTextContainer: {
            visible: !showDropdownModel()
        },
        modelText: {
            disabled: readOnly
        },
        myModelListedCheck: {
            onValueChange: onModelCheckBoxSelect,
            value: isUnlistedModel,
            visible: showUnlistedModel()
        },
        lookUpVinButtonContainer: {
            visible: !!(showVerifyVin() && (!vehicleVM.year.value || vehicleVM.year.value > 1980) && !vinValid() && !isUnlistedModel),
        },
        lookUpVinButton: {
            onClick: () => lookUpVinButton()
        },
        lookUpVinButtonGreenCheck: {
            visible: !!((isPrivatePassenger || isCollectible) && isPartialVinValid() && !isPrefillFullVinVerified())
        },
        lookUpVinButtonRequiredTextContainer: {
            visible: !!((isPrivatePassenger || isCollectible) && !isPartialVinValid() && !hasVin() && hasYear())
        },
        bodyTypeTrimContainer: {
            visible: !!((isPartialVinValid() && hasVin()) || isUnlistedModel || verifyVinClick || isVehicleYearPre1981())
        },
        bodyTypeDropdownContainer: {
            visible: !!((showBodyType() && !isVehicleYearPre1981() && hasVin()) && !isUnlistedModel && !readOnly && !vehicleVM.value.isVinValidated)
        },
        bodyTypeDropdown: {
            availableValues: validBodyTypes.length > 0 ? validBodyTypes : getGivenListDropdown(bodyTypelist.codes),
            disabled: true,
            onValueChange: onBodyTypeSelect
        },
        bodyTypeDropdown2Container: {
            visible: !!(showBodyType() && (isVehicleYearPre1981() || isUnlistedModel || readOnly || vehicleVM.value.isVinValidated))
        },
        bodyTypeDropdown2: {
            availableValues: validBodyTypes.length > 0 ? validBodyTypes : getGivenListDropdown(bodyTypelist.codes),
            disabled: !!(readOnly || (vehicleVM.value.isVinValidated && !isUnlistedModel)),
            onValueChange: onBodyTypeSelect
        },
        trimDropdownContainer: {
            visible: isTrimOptionsFieldVisible()
        },
        trimDropdown: {
            availableValues: trims,
            onValueChange: onTrimSelect,
            disabled: !!(readOnly && vehicleVM.trim_ML.value && vehicleVM.trim_ML.value.length > 0)
        },
        trimTextContainer: {
            visible: isFreeTextTrimFieldVisible()
        },
        trimText: {
            disabled: (readOnly && !trimNotFound) && (vehicleVM.value.isVinValidated && !isUnlistedModel)
        },
        licenseStateContainer: {
            className: _.get(vehicleVM, 'value.isAgentCreated', false) ? 'col-md-2 vertical-spacing-40 remove-form-group-spacing' : 'col-md-3 vertical-spacing-40 remove-form-group-spacing'
        },
        licenseState: {
            availableValues: getStatesList()
        },
        ccsRowContainer: {
            visible: showCCS()
        },
        ccs: {
            disabled: readOnly
        },
        horsepowerRowContainer: {
            visible: isOther && !isUMB_NP
        },
        policyInfo: {
            availableValues: getUnderlyingPolicies(),
            onValueChange: onPolicyInfoChange
        },
        excludeVehicleContainer: {
            visible: !(_.get(vehicleVM, 'value.fromPrefill_ML', false) && isUMB_NP)
        },
        licensedForRoad : {
            required:isUMB_NP,
            visible: isUMB_NP
        },
        vehicleCostNew: {
            required: isUMB_NP && (isPrivatePassenger || isVanSuv || isLightTruck || isPanelTruck || isFlatbedTruck || isCollectible || isMotorHome),
            visible: isUMB_NP && (isPrivatePassenger || isVanSuv || isLightTruck || isPanelTruck || isFlatbedTruck || isCollectible || isMotorHome),
            readOnly: (isUMB_NP && vinValid())
        },
        horsepowerNP: {
            visible: isUMB_NP,
            readOnly:(isUMB_NP && vinValid())
        }
    };

    const resolvers = {
        resolveComponentMap: {
            MLAlertGroupComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onValidate: onValidate,
            onVerifyVIN: verifyVIN
        }
    };

    const initializeTypeOptions = () => {
        setTypes(vehicleVM.type.aspects.availableValues.map((type) => {
            return {
                code: type.code,
                name: translator({ id: type.name })
            }
        }));
    };

    const initializeMakes = async () => {
        const year = _.get(vehicleVM, 'year.value');
        if (year) {
            if (isVehicleYearPre1981()) {
                setValidBodyTypes(getGivenListDropdown(bodyTypelist.codes));
            }
            const makeCodes = await fetchMakes(vehicleVM);
            updatePossibleMakes(makeCodes);
        }
    };

    const initializeModels = async () => {
        const make = _.get(vehicleVM, 'make.value');
        if (make) {
            const modelCodes = await fetchModels(vehicleVM);
            updatePossibleModels(modelCodes);
        }
    };

    const initializeBodyTypeAndTrims = async () => {
        const model = _.get(vehicleVM, 'model.value');
        if (model && !isVehicleYearPre1981()) {
            const vinPrefixes = await lookupVIN(vehicleVM);
            const trimCodes = getTrimsFromVinPrefixes(vinPrefixes);
            const bTypes = getBodyTypesFromVinPrefixes(vinPrefixes);

            updatetrims(trimCodes);
            setValidBodyTypes(bTypes);
        }
    }

    useEffect(() => {
        initializeTypeOptions();
        initializeMakes();
        initializeModels();
        initializeBodyTypeAndTrims();
        setOldSerial(_.get(vehicleVM, 'serialNumber.value', null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ViewModelForm
            uiProps={isGPA ? agentMetadata.componentContent : metadata.componentContent}
            model={vehicleVM}
            overrideProps={isGPA ? agentOverrideProps : overrideProps}
            onValueChange={writeValue}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
            showErrors={showValidationErrors}
        />
    );
}

MLPUPEditVehicleComponent.propTypes = {
    viewModelService: PropTypes.shape({
        clone: PropTypes.func
    }).isRequired,
    vehicleVM: PropTypes.shape({}).isRequired,
    quoteID: PropTypes.string,
    endorsementState: PropTypes.string,
    getUnderlyingPolicies: PropTypes.func,
    checkForDuplicateVin: PropTypes.func,
    checkforNonOwnedPolicy: PropTypes.func,
    onAddPolicy: PropTypes.func,
    isGPA: PropTypes.bool,
    showValidationErrors: PropTypes.bool,
    showProgressDialog: PropTypes.func,
    hideProgressDialog: PropTypes.func
};

export default MLPUPEditVehicleComponent;